/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
/**
 * Attempts to focus an element if it meets accessibility and visibility requirements.
 * Only focuses elements that are focusable (tabIndex > -1), visible (has offsetParent),
 * and not hidden from screen readers (no aria-hidden attribute).
 *
 * @param element - The HTML element to potentially focus, or null if no element provided
 */
export const focusOrIgnore = (element: HTMLElement | null) => {
  if (
    element &&
    element.tabIndex > -1 &&
    element.offsetParent !== null &&
    element.getAttribute('aria-hidden') !== 'true' &&
    window.getComputedStyle(element).visibility !== 'hidden'
  ) {
    element.focus();
  }
};

/**
 * Searches for the first interactive element within a given container element.
 * Looks for elements that can receive focus including buttons, links, form inputs,
 * and elements with explicit tabindex or contenteditable attributes.
 *
 * @param element - The container element to search within for interactive elements
 * @returns The first interactive element found, or null if none exists or container is null
 */
export const searchForFirstInteractiveElement = (element: HTMLElement | null) => {
  if (element) {
    const firstInteractiveElement = element.querySelector(
      'button:not([disabled]), a[href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [contenteditable="true"]'
    );
    return firstInteractiveElement as HTMLElement | null;
  }
  return null;
};

/**
 * Searches for and focuses the first interactive element within a given container element.
 * Combines the functionality of finding the first focusable element and attempting to focus it
 * while respecting accessibility constraints.
 *
 * @param element - The container element to search within for interactive elements
 */
export const searchAndFocusFirstInteractiveElement = (element: HTMLElement | null) => {
  const firstInteractiveElement = searchForFirstInteractiveElement(element);
  focusOrIgnore(firstInteractiveElement);
};

/**
 * Dictionary mapping semantic identifiers to their corresponding HTML element IDs.
 * Used for cross-referencing and maintaining consistent element identification across components.
 */
export const crossReferenceHtmlIdDictionary: Record<string, string> = {
  continueShoppingButtonId: 'continue-shopping-button',
  cartItemsContainerId: 'cart-items-container',
  cartItemIdPrefix: 'cart-item-id-',
} as const;
